"use client";

import React, { 
  createContext, 
  useContext, 
  useState, 
  useCallback, 
  useMemo,
  ReactNode 
} from 'react';
import { 
  LeaveReviewFormData, 
  validateFormStep
} from '@/lib/types/leave-review';

const STEP_ORDER = [
  'preparation',
  'verification',
  'property-details',
  'experience',
  'documents',
  'review',
  'submit'
] as const;

const CORE_STEPS = ['verification', 'property-details', 'experience', 'documents'] as const;

interface LeaveReviewNavigationContextType {
  currentStep: string;
  completedSteps: string[];
  setCurrentStep: (step: string) => void;
  completeStep: (stepName: string, formData: LeaveReviewFormData) => boolean;
  validateStep: (stepName: string, formData: LeaveReviewFormData) => boolean;
  getValidationErrors: (stepName: string, formData: LeaveReviewFormData) => string[];
  isStepCompleted: (stepName: string) => boolean;
  canNavigateToStep: (stepName: string, formData: LeaveReviewFormData) => boolean;
  getNextStep: () => string | null;
  getPrevStep: () => string | null;
  navigateNext: (formData: LeaveReviewFormData) => boolean;
  navigatePrev: () => boolean;
  getCompletionPercentage: () => number;
  resetNavigation: () => void;
}

const LeaveReviewNavigationContext = createContext<LeaveReviewNavigationContextType>({
  currentStep: 'preparation',
  completedSteps: [],
  setCurrentStep: () => {},
  completeStep: () => false,
  validateStep: () => false,
  getValidationErrors: () => [],
  isStepCompleted: () => false,
  canNavigateToStep: () => false,
  getNextStep: () => null,
  getPrevStep: () => null,
  navigateNext: () => false,
  navigatePrev: () => false,
  getCompletionPercentage: () => 0,
  resetNavigation: () => {},
});

interface LeaveReviewNavigationProviderProps {
  children: ReactNode;
  initialStep?: string;
  initialCompletedSteps?: string[];
}

export function LeaveReviewNavigationProvider({ 
  children, 
  initialStep = 'preparation',
  initialCompletedSteps = []
}: LeaveReviewNavigationProviderProps) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<string[]>(initialCompletedSteps);

  const validateStep = useCallback((stepName: string, formData: LeaveReviewFormData): boolean => {
    if (stepName === 'preparation') return true;
    
    const result = validateFormStep(stepName, formData);
    return result.isValid;
  }, []);

  const getValidationErrors = useCallback((stepName: string, formData: LeaveReviewFormData): string[] => {
    if (stepName === 'preparation') return [];
    
    const result = validateFormStep(stepName, formData);
    return result.errors;
  }, []);

  const isStepCompleted = useCallback((stepName: string): boolean => {
    return completedSteps.includes(stepName);
  }, [completedSteps]);

  const completeStep = useCallback((stepName: string, formData: LeaveReviewFormData): boolean => {
    if (!validateStep(stepName, formData)) return false;
    
    setCompletedSteps(prevCompleted => {
      const stepsSet = new Set(prevCompleted);
      stepsSet.add(stepName);
      return Array.from(stepsSet);
    });
    
    return true;
  }, [validateStep]);

  const canNavigateToStep = useCallback((stepName: string, formData: LeaveReviewFormData): boolean => {
    const stepIndex = STEP_ORDER.indexOf(stepName as any);
    if (stepIndex === -1) return false;

    if (stepName === 'preparation') return true;

    for (let i = 1; i < stepIndex; i++) {
      const prevStep = STEP_ORDER[i];
      if (!isStepCompleted(prevStep) && !validateStep(prevStep, formData)) {
        return false;
      }
    }

    return true;
  }, [isStepCompleted, validateStep]);

  const getNextStep = useCallback((): string | null => {
    const currentIndex = STEP_ORDER.indexOf(currentStep as any);
    if (currentIndex === -1 || currentIndex >= STEP_ORDER.length - 1) return null;
    
    return STEP_ORDER[currentIndex + 1];
  }, [currentStep]);

  const getPrevStep = useCallback((): string | null => {
    const currentIndex = STEP_ORDER.indexOf(currentStep as any);
    if (currentIndex <= 0) return null;
    
    return STEP_ORDER[currentIndex - 1];
  }, [currentStep]);

  const navigateNext = useCallback((formData: LeaveReviewFormData): boolean => {
    const nextStep = getNextStep();
    if (!nextStep) return false;

    if (!canNavigateToStep(nextStep, formData)) return false;

    if (currentStep !== 'preparation' && !completeStep(currentStep, formData)) {
      return false;
    }

    setCurrentStep(nextStep);
    return true;
  }, [currentStep, getNextStep, canNavigateToStep, completeStep]);

  const navigatePrev = useCallback((): boolean => {
    const prevStep = getPrevStep();
    if (!prevStep) return false;

    setCurrentStep(prevStep);
    return true;
  }, [getPrevStep]);

  const getCompletionPercentage = useCallback((): number => {
    const totalSteps = CORE_STEPS.length;
    const completedCoreSteps = completedSteps.filter(step => CORE_STEPS.includes(step as any));
    const completedCount = completedCoreSteps.length;
    return Math.round((completedCount / totalSteps) * 100);
  }, [completedSteps]);

  const resetNavigation = useCallback(() => {
    setCurrentStep('preparation');
    setCompletedSteps([]);
  }, []);

  const contextValue = useMemo(() => ({
    currentStep,
    completedSteps,
    setCurrentStep,
    completeStep,
    validateStep,
    getValidationErrors,
    isStepCompleted,
    canNavigateToStep,
    getNextStep,
    getPrevStep,
    navigateNext,
    navigatePrev,
    getCompletionPercentage,
    resetNavigation,
  }), [
    currentStep,
    completedSteps,
    completeStep,
    validateStep,
    getValidationErrors,
    isStepCompleted,
    canNavigateToStep,
    getNextStep,
    getPrevStep,
    navigateNext,
    navigatePrev,
    getCompletionPercentage,
    resetNavigation,
  ]);

  return (
    <LeaveReviewNavigationContext.Provider value={contextValue}>
      {children}
    </LeaveReviewNavigationContext.Provider>
  );
}

export function useLeaveReviewNavigation(): LeaveReviewNavigationContextType {
  const context = useContext(LeaveReviewNavigationContext);
  if (!context) {
    // Return safe defaults when called outside provider (e.g., landing page)
    return {
      currentStep: 'preparation',
      completedSteps: [],
      setCurrentStep: () => {},
      completeStep: () => false,
      validateStep: () => false,
      getValidationErrors: () => [],
      isStepCompleted: () => false,
      canNavigateToStep: () => false,
      getNextStep: () => null,
      getPrevStep: () => null,
      navigateNext: () => false,
      navigatePrev: () => false,
      getCompletionPercentage: () => 0,
      resetNavigation: () => {},
    };
  }
  return context;
} 